import { Capacitor, registerPlugin } from '@capacitor/core'
import { isNativeApp } from './native'

/** JS side of the in-app `Wifi` plugin (web/ios/App/App/WifiPlugin.swift). */
interface WifiPlugin {
  currentSsid(): Promise<{ ssid: string | null }>
  join(o: { ssid: string; passphrase?: string; once?: boolean }): Promise<{ joined: boolean; already?: boolean }>
  forget(o: { ssid: string }): Promise<void>
}

const Wifi = registerPlugin<WifiPlugin>('Wifi')

/** True in an app build that carries the plugin; older builds and the website fall back to Settings. */
export const wifiAvailable = isNativeApp && Capacitor.isPluginAvailable('Wifi')

/** The Wi-Fi network the phone is on, when iOS lets the app know. */
export async function currentSsid(): Promise<string | null> {
  if (!wifiAvailable) return null
  try {
    return (await Wifi.currentSsid()).ssid ?? null
  } catch {
    return null
  }
}

export class WifiError extends Error {
  code: string
  constructor(message: string, code: string) {
    super(message)
    this.code = code
  }
}

/** Join a network through iOS (which asks the user once); resolves when the phone is on it. */
export async function joinWifi(ssid: string, passphrase?: string): Promise<void> {
  try {
    await Wifi.join({ ssid, passphrase })
  } catch (e) {
    const err = e as { message?: string; code?: string }
    throw new WifiError(err.message ?? String(e), err.code ?? 'failed')
  }
}

export async function forgetWifi(ssid: string): Promise<void> {
  if (!wifiAvailable) return
  await Wifi.forget({ ssid }).catch(() => undefined)
}
